import {
  AvatarRootProps,
  ConditionalValue,
  FlexProps,
  Link,
  TextProps,
} from '@chakra-ui/react';
import {
  CustomFieldTypeEnum,
  IAddressSchema,
  IContactRequestSchema,
  IContactRespSchema,
  ICustomFieldResSchema,
} from '@monetize/types/app';
import {
  FilterType,
  FilterTypeOperator,
  Maybe,
  NestedKeyOf,
  SUPPORTED_CURRENCIES,
} from '@monetize/types/common';
import { formatCurrency, pluralize, toDateShort } from '@monetize/utils/core';
import {
  AppCellContext,
  AppColumn,
  AppColumnDef,
  AppColumnMeta,
  RowSelectionState,
} from './appTable';
import lodashGet from 'lodash/get';
import isDate from 'lodash/isDate';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import { ReactNode } from 'react';
import { Link as ReactDomLink } from 'react-router-dom';
import { flattenObject } from '../../../api/utils';
import { logger } from '../../../services/logger';
import { getAddress } from '../../../utils/address';
import { orderObjectsBy } from '../../../utils/misc';
import { DateDisplay } from '../DateDisplay';
import { MAvatar, MBox, MFlex, MIDCopyBox, MText, MTooltip } from './..';
import {
  booleanFilterFn,
  dateFilterFn,
  numberFilterFn,
  ownerFilterFn,
  recordSearchFilterFn,
  selectionFilterFn,
  setFilterFn,
  textFilterFn,
} from './dataTableFilters/dataTableFilterFns';
import {
  DateFilterComparison,
  NumberFilterComparison,
  OwnerFilterData,
  RecordSearchFilterData,
} from './DataTableTypes';

/**
 * Get the filter key for a column, prefer accessorKey and fallback to column.id
 * USer can override the key sent to the server with column.meta.filterKey
 */
export function getColumnFilterKey<TData extends object>(
  column: AppColumn<any, unknown>,
): string {
  return lodashGet(column, 'columnDef.accessorKey', column.id) as string;
}

interface TemplateOptions<TData = unknown, ExtraProps = TextProps> {
  noOfLines?: ConditionalValue<number>;
  /**
   * Optional extra properties to pass to the component
   */
  extraProps?: ExtraProps;
  /**
   * Value to use if the data[property] is null/undefined
   */
  fallback?: ReactNode;
  tooltipProperty?: keyof TData;
}

interface TemplateOptionsWithChildren<
  ExtraProps = TextProps,
> extends TemplateOptions<unknown, ExtraProps> {
  children?: (data: any) => ReactNode;
}

interface TemplateOptionsEnumDisplay<
  TData = unknown,
  ExtraProps = TextProps,
> extends TemplateOptions<TData, ExtraProps> {
  displayMap: { [key: string]: string };
  styleMap?: { [key: string]: Record<string, unknown> };
}

interface TemplateOptionsNameAndId<
  TData = unknown,
  ExtraProps = TextProps,
> extends TemplateOptions<TData, ExtraProps> {
  idProp: NestedKeyOf<TData>;
  nameProp: NestedKeyOf<TData>;
  /** If provided, the id will be a clickable link */
  idLinkFn?: (id: string, record: TData) => string;
  /** Whether to open link in new tab. Defaults to true */
  openInNewTab?: boolean;
}

interface TemplateOptionsCurrency<
  TData = unknown,
  ExtraProps = TextProps,
> extends TemplateOptions<TData, ExtraProps> {
  currencyProperty?: NestedKeyOf<TData>;
  currencyFallback?: string;
}

interface TemplateOptionsPeriod<
  TData = unknown,
  ExtraProps = TextProps,
> extends TemplateOptions<TData, ExtraProps> {
  endPeriodProperty?: NestedKeyOf<TData>;
}

interface TemplateOptionsContactAddress<
  TData = unknown,
  ExtraProps = TextProps,
> extends TemplateOptions<TData, ExtraProps> {
  contactProp: NestedKeyOf<TData>;
}

interface ColumnSizeOptions {
  /**
   * @default 150
   */
  min?: number;
  /**
   * @default 300
   */
  max?: number;
  /**
   * Default multiplier for column size - this is multiplied by number of characters in label
   * @default 9
   */
  multiplier?: number;
  /**
   * Default offset accounts for sorting and filtering icons
   * set to 0 if column does not allow sorting adn filtering
   * @default 25
   */
  offset?: number;
}

export type TableFilterMap = Array<{
  label: string;
  value: string | number | boolean;
}>;

export type TableData<T extends object> = T & {
  _textFilter?: string;
};

export const BLANK_TABLE_FILTER_KEY = '__BLANK__';

/**
 * Returns a header label and size/minSize for a column based on header length or provided options
 * Should be called and de-structured on a column definition
 *
 * @param headerLabel
 * @param fixedSizeOrOptions
 * @returns
 */
export function getColHeaderWithSize(
  headerLabel: string,
  fixedSizeOrOptions?: number | ColumnSizeOptions,
) {
  if (typeof fixedSizeOrOptions === 'number') {
    return {
      header: headerLabel,
      size: fixedSizeOrOptions,
      minSize: fixedSizeOrOptions,
    };
  }
  const size = getTableColumnSize(headerLabel, fixedSizeOrOptions);
  return {
    header: headerLabel,
    size: getTableColumnSize(headerLabel, fixedSizeOrOptions),
    minSize: size,
  };
}

export const getColumnFilterValuesForEnum = <T extends Record<string, string>>(
  enumVal: T,
  enumDisplay: {
    [key in keyof T]: string;
  },
): Array<{ label: string; value: string }> => {
  return Object.values(enumVal)
    .sort()
    .map((value) => ({ label: enumDisplay[value], value }));
};

export const getCurrencyFilterValues = (): Array<{
  label: string;
  value: string;
}> => {
  return Object.keys(SUPPORTED_CURRENCIES)
    .sort()
    .map((code) => ({ label: code, value: code }));
};

/**
 * Configures column filter settings for TanStack Table columns.
 * Returns filter configuration that should be spread into a column definition.
 *
 * @template TData - The table row data type
 * @param filterVariant - Filter type: 'BOOLEAN' | 'DATE' | 'SET' | 'SELECTION'
 * @param meta - Additional column metadata to merge with filter config
 * @returns Object with enableColumnFilter, filterFn, and meta to spread into ColumnDef
 */
export function getColHeaderFilters<TData extends object>(
  filterVariant: Exclude<
    AppColumnMeta<TData, unknown>['filterVariant'],
    undefined
  >,
  meta?: AppColumnMeta<TData, unknown>,
): Pick<AppColumnDef<any, any>, 'enableColumnFilter' | 'filterFn' | 'meta'> {
  switch (filterVariant) {
    case 'BOOLEAN':
      return {
        enableColumnFilter: true,
        filterFn: booleanFilterFn,
        meta: { filterVariant: 'BOOLEAN', ...meta },
      };
    case 'DATE':
      return {
        enableColumnFilter: true,
        filterFn: dateFilterFn,
        meta: { filterVariant: 'DATE', ...meta },
      };
    case 'SET':
      return {
        enableColumnFilter: true,
        filterFn: setFilterFn,
        meta: { filterVariant: 'SET', ...meta },
      };
    case 'SELECTION':
      return {
        enableColumnFilter: true,
        filterFn: selectionFilterFn,
        meta: { filterVariant: 'SELECTION', ...meta },
      };
    case 'TEXT':
      return {
        enableColumnFilter: true,
        filterFn: textFilterFn,
        meta: { filterVariant: 'TEXT', ...meta },
      };
    case 'NUMBER':
      return {
        enableColumnFilter: true,
        filterFn: numberFilterFn,
        meta: { filterVariant: 'NUMBER', ...meta },
      };
    case 'OWNER':
      return {
        enableColumnFilter: true,
        filterFn: ownerFilterFn,
        meta: { filterVariant: 'OWNER', ...meta },
      };
    case 'RECORD_SEARCH':
      return {
        enableColumnFilter: true,
        filterFn: recordSearchFilterFn,
        meta: { filterVariant: 'RECORD_SEARCH', ...meta },
      };
    default:
      return {};
  }
}

const getNumberFilterValue = ({
  comparison,
  firstValue,
  secondValue,
}: {
  comparison: string;
  firstValue?: string | null;
  secondValue?: string | null;
}) => {
  switch (comparison) {
    case NumberFilterComparison.EQUALS:
      return {
        min: firstValue || '',
        max: firstValue || '',
      };

    case NumberFilterComparison.GREATER_THAN:
    case NumberFilterComparison.GREATER_THAN_EQUAL:
      return {
        min: firstValue || '',
        max: '',
      };

    case NumberFilterComparison.LESS_THAN:
    case NumberFilterComparison.LESS_THAN_EQUAL:
      return {
        min: firstValue,
        max: '',
      };

    case NumberFilterComparison.BETWEEN_EXCLUSIVE:
    case NumberFilterComparison.BETWEEN_INCLUSIVE:
      return {
        min: firstValue || '',
        max: secondValue || '',
      };

    case NumberFilterComparison.IS_BLANK:
    case NumberFilterComparison.IS_NOT_BLANK:
      return {
        min: '',
        max: '',
      };

    default:
      return {
        min: firstValue || '',
        max: secondValue || '',
      };
  }
};

const getDateFilterValue = ({
  comparison,
  firstDate,
  secondDate,
}: {
  comparison: string;
  firstDate?: string | null;
  secondDate?: string | null;
}) => {
  switch (comparison) {
    case DateFilterComparison.EQUALS:
      // For equals, set both min and max to the same value to create a range
      return {
        min: firstDate || '',
        max: firstDate || '',
      };

    case DateFilterComparison.GREATER_THAN:
    case DateFilterComparison.GREATER_THAN_EQUAL:
      // For greater than comparisons, only set min
      return {
        min: firstDate || '',
        max: '',
      };

    case DateFilterComparison.LESS_THAN:
    case DateFilterComparison.LESS_THAN_EQUAL:
      // For less than comparisons, only set max
      return {
        min: firstDate,
        max: '',
      };

    case DateFilterComparison.BETWEEN_EXCLUSIVE:
    case DateFilterComparison.BETWEEN_INCLUSIVE:
      // For between comparisons, set both min and max
      return {
        min: firstDate || '',
        max: secondDate || '',
      };

    case DateFilterComparison.IS_BLANK:
    case DateFilterComparison.IS_NOT_BLANK:
      // Blank/Not Blank comparisons should not generate filter values
      // These should be handled separately or excluded from server filters
      return {
        min: '',
        max: '',
      };

    default:
      // Fallback for unknown comparison types
      return {
        min: firstDate || '',
        max: secondDate || '',
      };
  }
};

/**
 * Transforms initial FilterType[] array to a Map with operator preserved in values
 * Used to initialize columnFilters state from initialFilters prop
 *
 * @param initialFilters - Array of FilterType objects from parent component
 * @param columns - Column definitions used to reverse-map filterKey back to column ID
 * @returns Map of column IDs to filter values (with operator embedded for date filters)
 */
function normalizeBooleanFilterValue(value: unknown): unknown {
  if (value === true || value === 'true') {
    return 'CHECKED';
  }
  if (value === false || value === 'false') {
    return 'UNCHECKED';
  }
  return value;
}

export function transformInitialFiltersToMap(
  initialFilters: FilterType[],
  columns?: readonly AppColumnDef<any, any>[],
): Map<string, unknown> {
  const filterMap = new Map<string, unknown>();

  // Build reverse lookup: meta.filterKey → columnId
  // This handles cases where the server filter key differs from the column ID
  // e.g. column 'ownerName' has filterKey 'owner', so persisted filter key 'owner'
  // needs to map back to 'ownerName' for the internal columnFilters Map
  const filterKeyToColumnId = new Map<string, string>();
  const booleanFilterColumnIds = new Set<string>();
  const filterVariantByColumnId = new Map<string, string>();
  if (columns) {
    for (const column of columns) {
      const columnId = getColumnId(column);
      const filterKey = column.meta?.filterKey;
      if (columnId && filterKey) {
        filterKeyToColumnId.set(filterKey, columnId);
      }
      if (columnId && column.meta?.booleanFilter) {
        booleanFilterColumnIds.add(columnId);
      }
      if (columnId && column.meta?.filterVariant) {
        filterVariantByColumnId.set(columnId, column.meta.filterVariant);
      }
    }
  }

  initialFilters.forEach((filter) => {
    const mapKey = filterKeyToColumnId.get(filter.key) ?? filter.key;
    const filterVariant = filterVariantByColumnId.get(mapKey);

    if (
      filter.operator === FilterTypeOperator.EQUAL &&
      isString(filter.value) &&
      filter.value &&
      (filterVariant === 'OWNER' || filterVariant === 'RECORD_SEARCH')
    ) {
      const label = filter.label || filter.value;
      filterMap.set(
        mapKey,
        filterVariant === 'OWNER'
          ? ({ ownerId: filter.value, ownerName: label } as OwnerFilterData)
          : ({ id: filter.value, label } as RecordSearchFilterData),
      );
      return;
    }

    // SET filters are stored as arrays with IN operator — convert back to Set
    if (
      filter.operator === FilterTypeOperator.IN &&
      Array.isArray(filter.value)
    ) {
      filterMap.set(mapKey, new Set(filter.value));
    } else if (
      // For object values (like { min, max }), spread and add operator
      // This preserves the operator for date filters to read later
      typeof filter.value === 'object' &&
      filter.value !== null &&
      !Array.isArray(filter.value)
    ) {
      filterMap.set(mapKey, {
        ...filter.value,
        operator: filter.operator,
      });
    } else if (booleanFilterColumnIds.has(mapKey)) {
      // Opt-in BOOLEAN columns persist 'true'/'false'; convert to the filter's
      // internal CHECKED/UNCHECKED so the UI restores and round-trips correctly.
      filterMap.set(mapKey, normalizeBooleanFilterValue(filter.value));
    } else {
      // For strings, booleans, etc., store value as-is
      filterMap.set(mapKey, filter.value);
    }
  });

  return filterMap;
}

/**
 * Transforms internal column filter values to FilterType[] for server-side API consumption
 * Maps filter variants to their corresponding operators and value formats
 *
 * @param columnFilters - Map of column IDs to their filter values
 * @param getColumn - Function to retrieve column definition by ID
 * @returns Array of FilterType objects ready for API consumption
 */
export function transformFiltersForServer(
  columnFilters: Map<string, unknown>,
  getColumn: (
    columnId: string,
  ) => { columnDef: { meta?: AppColumnMeta<any, any> } } | undefined,
): FilterType[] {
  const transformedFilters: FilterType[] = [];

  columnFilters.forEach((value, key) => {
    // Find the column to check its filter variant
    const column = getColumn(key);
    const filterVariant = column?.columnDef.meta?.filterVariant;
    const filterKey = column?.columnDef.meta?.filterKey || key;

    switch (filterVariant) {
      case 'SET':
        // SET filters use IN operator with array value
        transformedFilters.push({
          key: filterKey,
          value: value instanceof Set ? Array.from(value) : (value as string[]),
          operator: FilterTypeOperator.IN,
        } as FilterType);
        break;

      case 'DATE':
        // DATE filters use specific operators based on comparison type
        if (value && typeof value === 'object' && 'firstDate' in value) {
          const dateFilter = value as {
            comparison: string;
            firstDate?: string | null;
            secondDate?: string | null;
          };

          // Skip IS_BLANK and IS_NOT_BLANK - these are client-side only
          if (
            dateFilter.comparison === DateFilterComparison.IS_BLANK ||
            dateFilter.comparison === DateFilterComparison.IS_NOT_BLANK
          ) {
            break;
          }

          // Map DateFilterComparison to FilterTypeOperator
          let operator: FilterTypeOperator;

          switch (dateFilter.comparison) {
            case DateFilterComparison.EQUALS:
              operator = FilterTypeOperator.EQ;
              break;

            case DateFilterComparison.GREATER_THAN:
              operator = FilterTypeOperator.GT;
              break;

            case DateFilterComparison.GREATER_THAN_EQUAL:
              operator = FilterTypeOperator.GTE;
              break;

            case DateFilterComparison.LESS_THAN:
              operator = FilterTypeOperator.LT;
              break;

            case DateFilterComparison.LESS_THAN_EQUAL:
              operator = FilterTypeOperator.LTE;
              break;

            case DateFilterComparison.BETWEEN_EXCLUSIVE:
            case DateFilterComparison.BETWEEN_INCLUSIVE:
              operator = FilterTypeOperator.BETWEEN;
              break;

            default:
              // Fallback to GLTE for backward compatibility
              operator = FilterTypeOperator.GLTE;
          }

          transformedFilters.push({
            key: filterKey,
            value: getDateFilterValue(dateFilter),
            operator,
          } as FilterType);
        }
        break;

      case 'NUMBER':
        // NUMBER filters use specific operators based on comparison type
        if (value && typeof value === 'object' && 'firstValue' in value) {
          const numberFilter = value as {
            comparison: string;
            firstValue?: string | null;
            secondValue?: string | null;
          };

          // Skip IS_BLANK and IS_NOT_BLANK - these are client-side only
          if (
            numberFilter.comparison === NumberFilterComparison.IS_BLANK ||
            numberFilter.comparison === NumberFilterComparison.IS_NOT_BLANK
          ) {
            break;
          }

          // Map NumberFilterComparison to FilterTypeOperator
          let operator: FilterTypeOperator;

          switch (numberFilter.comparison) {
            case NumberFilterComparison.EQUALS:
              operator = FilterTypeOperator.EQ;
              break;

            case NumberFilterComparison.GREATER_THAN:
              operator = FilterTypeOperator.GT;
              break;

            case NumberFilterComparison.GREATER_THAN_EQUAL:
              operator = FilterTypeOperator.GTE;
              break;

            case NumberFilterComparison.LESS_THAN:
              operator = FilterTypeOperator.LT;
              break;

            case NumberFilterComparison.LESS_THAN_EQUAL:
              operator = FilterTypeOperator.LTE;
              break;

            case NumberFilterComparison.BETWEEN_EXCLUSIVE:
            case NumberFilterComparison.BETWEEN_INCLUSIVE:
              operator = FilterTypeOperator.BETWEEN;
              break;

            default:
              operator = FilterTypeOperator.GLTE;
          }

          transformedFilters.push({
            key: filterKey,
            value: getNumberFilterValue(numberFilter),
            operator,
          } as FilterType);
        }
        break;

      case 'BOOLEAN':
        // BOOLEAN filters use EQUAL operator with boolean value
        if (value === 'CHECKED') {
          transformedFilters.push({
            key: filterKey,
            value: 'true',
            operator: FilterTypeOperator.EQUAL,
          } as FilterType);
        } else if (value === 'UNCHECKED') {
          transformedFilters.push({
            key: filterKey,
            value: 'false',
            operator: FilterTypeOperator.EQUAL,
          } as FilterType);
        }
        // Skip 'ALL' - no filter applied
        break;

      case 'OWNER':
        // OWNER filters use EQUAL operator with the selected owner ID
        if (
          value &&
          typeof value === 'object' &&
          'ownerId' in value &&
          (value as OwnerFilterData).ownerId
        ) {
          const { ownerId, ownerName } = value as OwnerFilterData;
          transformedFilters.push({
            key: filterKey,
            value: ownerId,
            operator: FilterTypeOperator.EQUAL,
            label: ownerName,
          } as FilterType);
        }
        break;

      case 'RECORD_SEARCH':
        // RECORD_SEARCH filters use EQUAL operator with the selected record id
        if (
          value &&
          typeof value === 'object' &&
          'id' in value &&
          (value as RecordSearchFilterData).id
        ) {
          const { id, label } = value as RecordSearchFilterData;
          transformedFilters.push({
            key: filterKey,
            value: id,
            operator: FilterTypeOperator.EQUAL,
            label,
          } as FilterType);
        }
        break;

      case 'SELECTION':
        // SELECTION is client-side only, skip for server-side
        break;

      case 'TEXT':
        // TEXT filters match on substring, unless the column opts into an
        // exact match because the API does not support `contains` on it
        if (isString(value) && value.trim()) {
          transformedFilters.push({
            key: filterKey,
            operator:
              column?.columnDef.meta?.textFilter?.operator === 'EQUAL'
                ? FilterTypeOperator.EQUAL
                : FilterTypeOperator.CONTAINS,
            value: value.trim(),
          } as FilterType);
        }
        break;

      default:
        // Unknown filter type - use EQUAL operator
        transformedFilters.push({
          key: filterKey,
          value: String(value),
          operator: FilterTypeOperator.EQUAL,
        } as FilterType);
        break;
    }
  });

  return transformedFilters;
}

/**
 * Returns a size for a column based on the length of the label
 */
export function getTableColumnSize(
  label: string,
  { min, max, multiplier, offset }: ColumnSizeOptions = {},
) {
  min = min ?? 150;
  max = max ?? 300;
  // Headers render uppercase + sort/filter icons sit to the right of the
  // label, so the per-character cost is higher than the natural-case
  // width and there's a fixed icon strip to reserve. Defaults tuned from
  // the "Next Invoice Date" (17 chars / 200px) calibration: 17*10+47=217
  // ≈ the user-confirmed 200px floor for that header.
  multiplier = multiplier ?? 10;
  offset = offset ?? 47;
  return Math.min(max, Math.max(min, label.length * multiplier + offset));
}

/**
 * Helper function for adding custom fields to table
 */
export function getCustomFieldsColumnGroup({
  id,
  header,
  accessorPathPrefix = 'customFields',
  customFieldMetadata,
}: {
  id: string;
  header: string;
  accessorPathPrefix?: string;
  customFieldMetadata: ICustomFieldResSchema[];
}): AppColumnDef<any, any> {
  return {
    id,
    header,
    columns: orderObjectsBy(customFieldMetadata, ['displayLabel']).map(
      (field, i): AppColumnDef<any, any> => {
        let headerFilterFn: Parameters<typeof getColHeaderFilters>[0] = 'SET';
        let cell = textBodyTemplate();
        switch (field.type) {
          case CustomFieldTypeEnum.CHECKBOX:
            headerFilterFn = 'BOOLEAN';
            cell = checkboxBodyTemplate();
            break;
          case CustomFieldTypeEnum.DATE:
            headerFilterFn = 'DATE';
            cell = dateBodyTemplate();
            break;
          case CustomFieldTypeEnum.NUMBER:
          case CustomFieldTypeEnum.SINGLE_LINE_TEXT:
          case CustomFieldTypeEnum.DROPDOWN:
          case CustomFieldTypeEnum.MULTI_SELECT:
          default:
            headerFilterFn = 'SET';
            break;
        }

        return {
          id: `${accessorPathPrefix}.${field.key}`,
          ...getColHeaderWithSize(field.displayLabel, {
            min: i === 0 ? 175 : 200,
          }),
          accessorFn: (originalRow) =>
            lodashGet(originalRow, `${accessorPathPrefix}.${field.key}`),
          cell,
          enableSorting: true,
          sortFn: 'text',
          ...getColHeaderFilters(headerFilterFn),
        };
      },
    ),
  };
}

/**
 * Drops selected row ids that are no longer present in the dataset.
 *
 * Returns the *same reference* when nothing changed. The caller passes this to
 * `setRowSelection`, so returning a fresh object unconditionally would queue an
 * extra render on every data change - including the common case of no rows
 * being selected at all.
 */
export function pruneRowSelection(
  previousSelection: RowSelectionState,
  isValidRowId: (rowId: string) => boolean,
): RowSelectionState {
  const previousKeys = Object.keys(previousSelection);
  if (previousKeys.length === 0) {
    return previousSelection;
  }

  const nextSelection: RowSelectionState = {};
  previousKeys.forEach((key) => {
    if (isValidRowId(key)) {
      nextSelection[key] = previousSelection[key];
    }
  });

  return Object.keys(nextSelection).length === previousKeys.length
    ? previousSelection
    : nextSelection;
}

/**
 * Index of the last item whose column is not pinned, or -1 when every column is
 * pinned. The item at this index is the one that flexes to fill leftover
 * horizontal space.
 *
 * Visible cells and headers are ordered `[...start, ...center, ...end]`, so a
 * single reverse scan is equivalent to asking "is everything after me pinned?"
 * of each item in turn — but O(n) instead of O(n²) per row.
 */
export function findLastNonPinnedIndex(
  items: { column: { getIsPinned: () => false | 'start' | 'end' } }[],
): number {
  for (let index = items.length - 1; index >= 0; index--) {
    if (!items[index].column.getIsPinned()) {
      return index;
    }
  }
  return -1;
}

/**
 * Helper function to extract column ID from column definition
 */
export function getColumnId(
  column: AppColumnDef<any, any>,
): string | undefined {
  if (column.id) {
    return column.id;
  }
  if ('accessorKey' in column && typeof column.accessorKey === 'string') {
    return column.accessorKey;
  }
  return undefined;
}

/**
 * Processes data to optimize for table display
 * * All nested objects are flattened using "." as separator
 * * All text keys are concatenated into a single lowercase string for text search
 * * Filter values are generated for all columns with unique values to allow an excel-like filter
 *
 * @param data
 * @param filterDataDisplayMap
 * @param textSearchKeys
 * @returns
 */
export function prepareTableData<T extends object>(
  data: T[],
  filterDataDisplayMap: { [key: string]: { [value: string]: string } } = {},
  textSearchKeys: string[] = [],
  /**
   * If true, skips generating filter values for columns
   * Set to true when not using SET filters to improve performance
   * This is especially useful for large datasets
   */
  skipFilterValuePreprocessing = false,
  columns?: readonly AppColumnDef<any, any>[],
): {
  filterValues: Map<string, TableFilterMap>;
  data: TableData<T>[];
} {
  const filterValuesTemp = new Map<string, Set<string | number | boolean>>();
  const filterValues = new Map<string, TableFilterMap>();

  // Build a map of column IDs to predefined filter values
  const predefinedValuesMap = new Map<
    string,
    Array<{ label: string; value: string | number | boolean }>
  >();
  if (columns) {
    columns.forEach((column) => {
      const columnId = getColumnId(column);
      const predefinedValues = column.meta?.predefinedFilterValues;
      if (predefinedValues && columnId) {
        predefinedValuesMap.set(columnId, predefinedValues);
      }
    });
  }

  /**
   * The rows are returned as-is. `TableData` only ever added `_textFilter`,
   * which is not yet implemented (see the TODO below) and is read nowhere, so
   * copying every row here bought nothing. Returning the original array also
   * keeps `row.original` referentially identical to the fetched object.
   *
   * TODO: for global search, build a `_textFilter` string per row from
   * `textSearchKeys` - at which point this will need to map again.
   */
  const enrichedData = data as TableData<T>[];

  if (!skipFilterValuePreprocessing) {
    data.forEach((originalRow) => {
      // `flattenObject` already resolved each dotted path to its value, so
      // read the entries directly rather than re-resolving via lodash `get`.
      Object.entries<unknown>(flattenObject(originalRow)).forEach(
        ([key, value]) => {
          // Skip auto-extraction if column has predefined values
          if (predefinedValuesMap.has(key)) {
            return;
          }

          if (!filterValuesTemp.has(key)) {
            filterValuesTemp.set(key, new Set());
          }

          if (
            typeof value === 'string' ||
            typeof value === 'number' ||
            typeof value === 'boolean'
            // TODO: other data types?
          ) {
            filterValuesTemp.get(key)?.add(value);
          } else if (value === null) {
            filterValuesTemp.get(key)?.add(BLANK_TABLE_FILTER_KEY);
          }
        },
      );
    });
  }

  // Process predefined values first (already have label & value)
  predefinedValuesMap.forEach((values, key) => {
    filterValues.set(key, orderObjectsBy(values, ['label']));
  });

  if (skipFilterValuePreprocessing) {
    return {
      filterValues,
      data: enrichedData,
    };
  }

  // Process auto-extracted values (apply filterDataDisplayMap for labels)
  Array.from(filterValuesTemp.keys()).forEach((key) => {
    // Skip if already processed as predefined
    if (filterValues.has(key)) {
      return;
    }

    const values = filterValuesTemp.get(key);
    if (values) {
      filterValues.set(
        key,
        orderObjectsBy(
          Array.from(values).map((value) => {
            if (value === BLANK_TABLE_FILTER_KEY) {
              return {
                label: '(Blanks)',
                value: BLANK_TABLE_FILTER_KEY,
              };
            }
            return {
              label:
                filterDataDisplayMap[key]?.[String(value)] ?? String(value),
              value,
            };
          }),
          ['label'],
        ),
      );
    }
  });

  return {
    filterValues,
    data: enrichedData,
  };
}

/**
 * CUSTOM FILTER FUNCTIONS
 *
 * ONLY APPLIES TO BROWSER SIDE FILTERING
 *
 * Used if the built-ins do not meet the requirements
 * {@link https://tanstack.com/table/latest/docs/guide/column-filtering#filterfns}
 */

/**
 * Filter for SELECTION filter variant
 * Filter for the checkbox selection column
 */
// export const selectionFilterFn = (
//   row: AppRow<any>,
//   columnId: string,
//   filterValue: SelectionOption,
//   addMeta: (meta: any) => void,
// ) => {
//   if (!filterValue || filterValue === 'ALL') {
//     return true;
//   }
//   const isSelected = row.getIsSelected();
//   if (
//     (filterValue === 'SELECTED' && isSelected) ||
//     (filterValue === 'UNSELECTED' && !isSelected)
//   ) {
//     return true;
//   }
//   return false;
// };

/**
 * Filter for SET filter variant
 */
// export const setFilterFn = (
//   row: AppRow<any>,
//   columnId: string,
//   filterValue: Set<any>,
//   addMeta: (meta: any) => void,
// ) => {
//   if (!filterValue || !filterValue.size) {
//     return true;
//   }
//   const value = row.getValue<any>(columnId) ?? BLANK_TABLE_FILTER_KEY;
//   return filterValue.has(value);
// };

// /**
//  * Filter for DATE filter variant
//  * Any datetime value is converted to a date string before comparison
//  */
// export const dateFilterFn = (
//   row: AppRow<any>,
//   columnId: string,
//   filterValue: DateFilterData,
//   addMeta: (meta: any) => void,
// ) => {
//   const parseResults = DateFilterDataSchema.safeParse(filterValue);
//   if (!parseResults.success) {
//     return true;
//   }
//   let value = row.getValue<string | null>(columnId) ?? null;
//   const { comparison, firstDate, secondDate } = parseResults.data;

//   if (!firstDate || (isBetweenComparison(comparison) && !secondDate)) {
//     return true;
//   }

//   if (!value) {
//     return false;
//   }
//   // strip time to allow for proper comparison with datetime
//   value = value.substring(0, 10);

//   switch (comparison) {
//     case 'EQUALS':
//       return value === firstDate;
//     case 'GREATER_THAN':
//       return value > firstDate;
//     case 'GREATER_THAN_EQUAL':
//       return value >= firstDate;
//     case 'LESS_THAN':
//       return value < firstDate;
//     case 'LESS_THAN_EQUAL':
//       return value <= firstDate;
//     case 'BETWEEN_EXCLUSIVE': {
//       if (!secondDate) {
//         return true;
//       }
//       return value > firstDate && value < secondDate;
//     }
//     case 'BETWEEN_INCLUSIVE': {
//       if (!secondDate) {
//         return true;
//       }
//       return value >= firstDate && value <= secondDate;
//     }
//     default:
//       return true;
//   }
// };

/**
 * CELL TEMPLATE RENDERERS
 */

export function idBodyTemplate<TData extends object, TValue>({
  extraProps,
  fallback,
  noOfLines = 1,
}: TemplateOptions = {}) {
  return (data: AppCellContext<TData, TValue>) => {
    const value = data.getValue();
    try {
      return (
        (isString(value) && (
          <MIDCopyBox copyValue={value} displayIcon={false} {...extraProps} />
        )) ||
        fallback
      );
    } catch (ex) {
      logger.warn('Could not generate template', {
        data,
        columns: data.cell.column.id,
        ex,
      });
    }
  };
}

export function idWithExtraBodyTemplate<TData extends object, TValue>({
  extraProps,
  fallback,
  noOfLines = 1,
  children,
}: TemplateOptionsWithChildren<FlexProps> = {}) {
  return (data: AppCellContext<TData, TValue>) => {
    const value = data.getValue();
    try {
      return (
        (isString(value) && (
          <MFlex alignItems="center" columnGap={2} {...extraProps}>
            <MIDCopyBox copyValue={value} displayIcon={false} />
            {children?.(data)}
          </MFlex>
        )) ||
        fallback
      );
    } catch (ex) {
      logger.warn('Could not generate template', {
        data,
        columns: data.cell.column.id,
        ex,
      });
    }
  };
}

export function textBodyTemplate<TData extends object, TValue>({
  extraProps,
  fallback,
  noOfLines = 3,
}: TemplateOptions = {}) {
  return (data: AppCellContext<TData, TValue>) => {
    const value = data.getValue();
    try {
      return (
        ((isString(value) || isNumber(value)) && (
          <MText lineClamp={noOfLines} {...extraProps}>
            {value}
          </MText>
        )) ||
        fallback
      );
    } catch (ex) {
      logger.warn('Could not generate template', {
        data,
        columns: data.cell.column.id,
        ex,
      });
    }
  };
}

export function checkboxBodyTemplate<TData extends object, TValue>({
  extraProps,
  fallback,
}: {
  fallback?: boolean;
  extraProps?: TextProps;
} = {}) {
  return (data: AppCellContext<TData, TValue>) => {
    const value = !!(data.getValue() ?? fallback);
    try {
      return <MText {...extraProps}>{value ? 'Yes' : 'No'}</MText>;
    } catch (ex) {
      logger.warn('Could not generate template', {
        data,
        columns: data.cell.column.id,
        ex,
      });
    }
  };
}

export function contactBodyTemplate<TData extends object>({
  extraProps,
  fallback,
}: TemplateOptions = {}) {
  return (data: AppCellContext<TData, IContactRequestSchema>) => {
    try {
      const value = data.getValue();
      if (!value) {
        return fallback;
      }
      const { fullName, email, phone } = value;

      return (
        <MBox {...extraProps}>
          <MText lineClamp={3} fontWeight={600}>
            {fullName}
          </MText>
          <MText lineClamp={2}>{email}</MText>
          {phone && <MText lineClamp={2}>{phone}</MText>}
        </MBox>
      );
    } catch (ex) {
      logger.warn('Could not generate template', {
        data,
        columns: data.cell.column.id,
        ex,
      });
    }
  };
}

export function emailListBodyTemplate<TData extends object>({
  extraProps,
  fallback,
}: TemplateOptions = {}) {
  return (data: AppCellContext<TData, string[]>) => {
    try {
      const value = data.getValue() || [];
      if (!Array.isArray(value)) {
        return fallback;
      }

      return (
        <MText lineClamp={5} fontWeight={600}>
          {value.join(', ')}
        </MText>
      );
    } catch (ex) {
      logger.warn('Could not generate template', {
        data,
        columns: data.cell.column.id,
        ex,
      });
    }
  };
}

export function addressWithContactBodyTemplate<TData extends object>({
  contactProp,
  extraProps,
  fallback,
}: TemplateOptionsContactAddress<TData>) {
  return (data: AppCellContext<TData, IAddressSchema>) => {
    try {
      const value = data.getValue();
      const contact =
        (lodashGet(data.row.original, contactProp) as IContactRespSchema) ||
        undefined;
      if (!value && !contact) {
        return fallback;
      }

      const address = getAddress(value);

      return (
        <MBox {...extraProps}>
          {contact.fullName && <MText lineClamp={1}>{contact.fullName}</MText>}
          {contact.email && (
            <MText lineClamp={1} mb={1}>
              {contact.email}
            </MText>
          )}
          <MText lineClamp={1}>{address.addressLine1}</MText>
          <MText lineClamp={1}>{address.addressLine1}</MText>
          {address.addressLine2 && (
            <MText lineClamp={1}>{address.addressLine2}</MText>
          )}
          {address.otherAddress && (
            <MText lineClamp={1}>{address.otherAddress}</MText>
          )}
        </MBox>
      );
    } catch (ex) {
      logger.warn('Could not generate template', {
        data,
        columns: data.cell.column.id,
        ex,
      });
    }
  };
}

export function addressBodyTemplate<TData extends object>({
  extraProps,
  fallback,
}: TemplateOptions = {}) {
  return (data: AppCellContext<TData, IAddressSchema>) => {
    try {
      const value = data.getValue();
      if (!value) {
        return fallback;
      }

      const address = getAddress(value);

      return (
        <MBox {...extraProps}>
          <MText lineClamp={1}>{address.addressLine1}</MText>
          {address.addressLine2 && (
            <MText lineClamp={1}>{address.addressLine2}</MText>
          )}
          {address.otherAddress && (
            <MText lineClamp={1}>{address.otherAddress}</MText>
          )}
        </MBox>
      );
    } catch (ex) {
      logger.warn('Could not generate template', {
        data,
        columns: data.cell.column.id,
        ex,
      });
    }
  };
}

/**
 * Pass in with name property and the id will be obtained
 */
export function nameWithIdBodyTemplate<TData extends object, TValue>({
  extraProps,
  fallback,
  idProp,
  nameProp,
  noOfLines = 3,
  idLinkFn,
  openInNewTab = false,
}: TemplateOptionsNameAndId<TData, TValue>) {
  return (data: AppCellContext<TData, TValue>) => {
    try {
      const id = lodashGet(data.row.original, idProp);
      const name = lodashGet(data.row.original, nameProp);
      if (!isString(id) && !name) {
        return <MText {...extraProps}>{fallback}</MText>;
      }
      const link =
        idLinkFn && isString(id) ? idLinkFn(id, data.row.original) : undefined;
      return (
        <MBox width="auto" {...extraProps}>
          {isString(name) && (
            <MText fontWeight="500" whiteSpace="normal" lineClamp={noOfLines}>
              {name}
            </MText>
          )}
          {isString(id) &&
            (link ? (
              <Link
                fontWeight="400"
                whiteSpace="normal"
                // Legacy IDs are full UUIDs with no whitespace, so
                // whiteSpace=normal alone won't wrap them; overflowWrap
                // breaks the string at any character when it would
                // otherwise overflow the cell.
                overflowWrap="anywhere"
                _hover={{ textDecoration: 'underline' }}
                asChild
              >
                <ReactDomLink
                  to={link}
                  target={openInNewTab ? '_blank' : undefined}
                  onClick={(ev) => ev.stopPropagation()}
                >
                  {id}
                </ReactDomLink>
              </Link>
            ) : (
              <MIDCopyBox
                copyValue={id || ''}
                displayIcon={false}
                overflowWrap="anywhere"
                {...extraProps}
              />
            ))}
        </MBox>
      );
    } catch (ex) {
      logger.warn('Could not generate template', {
        data,
        columns: data.cell.column.id,
        ex,
      });
    }
  };
}

export function textWithTooltipBodyTemplate<TData extends object, TValue>({
  extraProps,
  fallback,
  tooltipProperty,
  noOfLines = 1,
}: TemplateOptions = {}) {
  return (data: AppCellContext<TData, TValue>) => {
    const value = data.getValue();
    const tooltip = (data.row.original as any)[tooltipProperty as any];
    try {
      return (
        (isString(value) && (
          <MTooltip
            label={isString(tooltip) ? tooltip : null}
            placement="bottom-end"
          >
            <MText lineClamp={noOfLines} {...extraProps}>
              {value}
            </MText>
          </MTooltip>
        )) ||
        fallback
      );
    } catch (ex) {
      logger.warn('Could not generate template', {
        data,
        columns: data.cell.column.id,
        ex,
      });
    }
  };
}

export function textWithExtraBodyTemplate<TData extends object, TValue>({
  extraProps,
  fallback,
  noOfLines = 1,
  children,
}: TemplateOptionsWithChildren<FlexProps> = {}) {
  return (data: AppCellContext<TData, TValue>) => {
    const value = data.getValue();
    try {
      return (
        (isString(value) && (
          <MFlex align="center" columnGap={2} {...extraProps}>
            <MText lineClamp={noOfLines}>{value}</MText>
            {children?.(data)}
          </MFlex>
        )) ||
        fallback
      );
    } catch (ex) {
      logger.warn('Could not generate template', {
        data,
        columns: data.cell.column.id,
        ex,
      });
    }
  };
}

export function currencyBodyTemplate<TData extends object, TValue>({
  extraProps,
  noOfLines = 1,
  currencyProperty,
  currencyFallback = 'USD',
  fallback,
}: TemplateOptionsCurrency<TData, TValue> = {}) {
  return (data: AppCellContext<TData, TValue>) => {
    const value = data.getValue();
    const possibleValue = currencyProperty
      ? lodashGet(data.row.original, currencyProperty, currencyFallback)
      : currencyFallback;
    const currency = isString(possibleValue) ? possibleValue : currencyFallback;
    try {
      return (
        ((isString(value) || isNumber(value)) && (
          <MText lineClamp={noOfLines} {...extraProps}>
            {formatCurrency(value, { currency })}
          </MText>
        )) ||
        fallback
      );
    } catch (ex) {
      logger.warn('Could not generate template', {
        data,
        columns: data.cell.column.id,
        ex,
      });
    }
  };
}

export function dateBodyTemplate<TData extends object, TValue>({
  extraProps,
  noOfLines = 1,
  fallback,
}: TemplateOptions = {}) {
  return (data: AppCellContext<TData, TValue>) => {
    const value = data.getValue();
    try {
      return (
        ((isString(value) || isDate(value)) && (
          <MText lineClamp={noOfLines} {...extraProps}>
            {toDateShort(value)}
          </MText>
        )) ||
        fallback
      );
    } catch (ex) {
      logger.warn('Could not generate template', {
        data,
        columns: data.cell.column.id,
        ex,
      });
    }
  };
}

export function dateTimeBodyTemplate<TData extends object, TValue>({
  extraProps,
  noOfLines = 1,
  fallback,
}: TemplateOptions = {}) {
  return (data: AppCellContext<TData, TValue>) => {
    const value = data.getValue();
    try {
      return (
        ((isString(value) || isDate(value)) && (
          <DateDisplay
            date={value}
            textProps={{ lineClamp: noOfLines, ...extraProps }}
            timezoneConfigKey="userTimezone"
          />
        )) ||
        fallback
      );
    } catch (ex) {
      logger.warn('Could not generate template', {
        data,
        columns: data.cell.column.id,
        ex,
      });
    }
  };
}

export function datePeriodBodyTemplate<TData extends object, TValue>({
  extraProps,
  noOfLines = 1,
  fallback,
  endPeriodProperty,
}: TemplateOptionsPeriod<TData, TValue> = {}) {
  return (data: AppCellContext<TData, TValue>) => {
    const startDate = data.getValue();
    const endDate = endPeriodProperty
      ? (lodashGet(data.row.original, endPeriodProperty) as Maybe<
          string | Date
        >)
      : undefined;
    if (!startDate && !endDate) {
      return fallback;
    }
    try {
      const startDateStr =
        isString(startDate) || isDate(startDate)
          ? toDateShort(startDate)
          : null;
      const endDateStr =
        isString(endDate) || isDate(endDate) ? toDateShort(endDate) : null;

      return (
        <MText lineClamp={noOfLines} {...extraProps}>
          {startDateStr}
          {!!startDateStr && !!endDateStr && ' - '}
          {endDateStr}
        </MText>
      );
    } catch (ex) {
      logger.warn('Could not generate template', {
        data,
        columns: data.cell.column.id,
        ex,
      });
    }
  };
}

export function userAvatarBodyTemplate<TData extends object, TValue>({
  extraProps,
  fallback,
}: TemplateOptions<TData, AvatarRootProps> = {}) {
  return (data: AppCellContext<TData, TValue>) => {
    const value = data.getValue();
    try {
      return (
        (isString(value) && (
          <MFlex justify="center" align="center">
            <MTooltip shouldWrapChildren label={value} placement="bottom-start">
              <MAvatar name={value} mr="0" {...extraProps} />
            </MTooltip>
          </MFlex>
        )) ||
        fallback
      );
    } catch (ex) {
      logger.warn('Could not generate template', {
        data,
        columns: data.cell.column.id,
        ex,
      });
    }
  };
}

// TODO: figure out how to strongly type this
export function enumDisplayBodyTemplate<TData extends object, TValue>({
  noOfLines = 1,
  extraProps,
  fallback,
  displayMap,
  styleMap,
}: TemplateOptionsEnumDisplay<TData, TValue>) {
  return (data: AppCellContext<TData, TValue>) => {
    const rawValue = data.getValue();
    const value = displayMap[rawValue as any];
    const dynamicStyle = styleMap ? styleMap[rawValue as any] : {};
    try {
      return (
        (isString(value) && (
          <MText
            lineClamp={noOfLines}
            {...dynamicStyle}
            {...extraProps}
            maxW="fit-content"
            h="18px"
            lineHeight="18px"
          >
            {value}
          </MText>
        )) ||
        fallback
      );
    } catch (ex) {
      logger.warn('Could not generate template', {
        data,
        columns: data.cell.column.id,
        ex,
      });
    }
  };
}

export function durationInMonthsTemplate<TData extends object>({
  fallback,
}: TemplateOptions = {}) {
  return (data: AppCellContext<TData, number>) => {
    try {
      const value = data.getValue();
      if (typeof value !== 'number') {
        return fallback;
      }

      return (
        <MText lineClamp={1}>
          {value} {pluralize('month', value)}
        </MText>
      );
    } catch (ex) {
      logger.warn('Could not generate template', {
        data,
        columns: data.cell.column.id,
        ex,
      });
    }
  };
}
